import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/** All sign-in motion plays on the first visit in a browser tab; later visits show the finished page. */
const PLAY_ONCE_PER_SESSION = true;
const PLAYED_KEY = "integer-intro-played";
const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
/** Logo box size, and how far its top sits above the middle of the screen (`.login-intro-mark`). */
const MARK_SIZE = 220;
const MARK_TOP = 130;

/** What the intro is doing. Mirrored to `data-intro` on the page root. */
export type IntroState = "playing" | "forced" | "reduced-motion" | "played-before" | "skipped" | "done";

const readFlag = () => {
  try {
    return sessionStorage.getItem(PLAYED_KEY) === "1";
  } catch {
    return false;
  }
};

const writeFlag = () => {
  try {
    sessionStorage.setItem(PLAYED_KEY, "1");
  } catch {
    // Storage is blocked: the intro simply plays again on the next visit.
  }
};

/**
 * The one decision for a page load, taken before the first render. The motion plays on the
 * first visit in a tab, and every later load shows the finished page. `?intro=replay` plays
 * the full intro again for testing, ignoring the flag and reduced motion.
 */
export function decideIntro(): IntroState {
  if (new URLSearchParams(window.location.search).get("intro") === "replay") return "forced";
  if (window.matchMedia(REDUCED_MOTION).matches) return "reduced-motion";
  if (PLAY_ONCE_PER_SESSION && readFlag()) return "played-before";
  return "playing";
}

/**
 * Drives the sign-in intro. Returns the logo's ref, the current state, whether the intro is
 * still running, and `complete`, which the last sweep calls when it ends. A press or key
 * ends the intro early as "skipped".
 */
export function useSignInIntro() {
  const markRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<IntroState>(decideIntro);
  const running = state === "playing" || state === "forced";

  // Marked as soon as the motion starts, so a reload at any point counts as a repeat visit.
  useEffect(() => {
    if (PLAY_ONCE_PER_SESSION && running) writeFlag();
  }, [running]);

  const end = useCallback((next: "skipped" | "done") => {
    setState((s) => (s === "playing" || s === "forced" ? next : s));
  }, []);
  const skip = useCallback(() => end("skipped"), [end]);
  const complete = useCallback(() => end("done"), [end]);

  useEffect(() => {
    if (import.meta.env.DEV) console.info("[intro]", state);
  }, [state]);

  // Docks the logo on its slot before the first paint, and keeps it on the slot as the page resizes or scrolls.
  useLayoutEffect(() => {
    const mark = markRef.current;
    if (!mark) return;
    const place = () => {
      const slot = document.querySelector("[data-logo-slot]");
      if (!slot) return;
      const box = slot.getBoundingClientRect();
      // The slot is hidden below lg, so there is nothing to dock on.
      if (box.width === 0) return;
      const startX = document.documentElement.clientWidth / 2;
      const startY = document.documentElement.clientHeight / 2 - MARK_TOP + MARK_SIZE / 2;
      // Runtime measurements that change with the viewport, so they cannot be static classes.
      mark.style.setProperty("--dx", `${box.left + box.width / 2 - startX}px`);
      mark.style.setProperty("--dy", `${box.top + box.height / 2 - startY}px`);
      mark.style.setProperty("--s", String(box.width / MARK_SIZE));
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, { passive: true });
    // The hero column can change height after this first measurement (LoginHero sets the carousel
    // scale after mount), which moves the slot, so re-dock whenever that column resizes.
    const column = document.querySelector("[data-logo-slot]")?.parentElement;
    const observer = column ? new ResizeObserver(() => place()) : null;
    if (column) observer?.observe(column);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place);
      observer?.disconnect();
    };
  }, []);

  // Any press or key skips the rest of the intro.
  useEffect(() => {
    if (!running) return;
    window.addEventListener("pointerdown", skip);
    window.addEventListener("keydown", skip);
    return () => {
      window.removeEventListener("pointerdown", skip);
      window.removeEventListener("keydown", skip);
    };
  }, [running, skip]);

  return { markRef, state, running, complete };
}
